/**
 * meshopt 压缩 GLB 的运行时转码（浏览器端，传输压缩、加载前解码）。
 *
 * 背景：PlayCanvas 引擎（截至 2.23.0）不支持 EXT_meshopt_compression 与
 * KHR_mesh_quantization，但 meshopt 压缩 GLB 的下载体积只有解压后的
 * 1/6 左右（如 spot：8MB 压缩 vs 51MB float32）。为了手机端弱网体验，
 * 磁盘与网络只走压缩文件，进入 PlayCanvas 前在浏览器里解码：
 *
 *   fetch 原始压缩字节（HTTP 缓存友好，网络仅传输压缩数据；带停滞看门狗）
 *     → 检测 JSON chunk 的 extensionsUsed
 *     → 含压缩扩展：gltf-transform WebIO 读入（meshopt wasm 解码）
 *       → dequantize（量化 int16 → float32）
 *       → 重建标准 GLB → Blob URL 交给引擎照常解析
 *     → 不含压缩扩展：原 URL 直通零开销（不 fetch、不加载转码库）。
 *
 * 转码库全部动态 import：不打进主 bundle，首次遇到压缩模型才拉取
 * chunk（HTTP 缓存后后续会话零成本）。同 URL 只转码一次（结果 Map 缓存
 * Blob URL；?v= 版本参数变化会得到新 URL，自动重转）；并发首载去重
 * （in-flight Map），预览舞台与主应用同时首次请求同一模型不会双转码。
 * 转码管线在专用 Web Worker 中执行（glbMeshoptWorker）：spot 级模型
 * （8MB 压缩 → 51MB float32）解码期间的 ~160MB 瞬时峰值落在 Worker
 * 独立堆且随 Worker 销毁整体归还——主线程只经 transferable 零拷贝接收
 * 最终字节，避免叠加 gsplat 场景内存后触发 iOS jetsam 闪退。
 * 引擎解析完成后由 AvatarAssetCache 调 retireDecoded 退役 Blob——解码
 * 字节（spot 53.8MB 级）不再常驻内存，切走形象即释放，重载走重新转码
 * （HTTP 缓存保证网络零成本）。
 *
 * 接入点：AvatarAssetCache.acquireAvatar（主应用）与 AvatarStage._load
 * （预览独立应用）——两处都在把 URL 交给 PlayCanvas 前调用
 * ensurePlayableGlb 换取最终可加载地址。
 */

/** 已转码：原始 URL → Blob URL。retireDecoded 后移除（重载自动重转）。 */
const decoded = new Map<string, string>();
/** 转码进行中：原始 URL → in-flight Promise，并发去重防双转码。 */
const pending = new Map<string, Promise<string>>();

/** fetch 停滞看门狗窗口：这么久没有新字节即判死连接并中止。慢而活的
 *  弱网（字节持续到达）不受任何总量限制——只有「卡死」会快速失败走
 *  引擎自身的加载报错路径（再由 AvatarController 的总超时兜底占位）。 */
const FETCH_STALL_MS = 20000;
/** Blob 退役到撤销的延迟：给并发消费者（预览舞台）在途 fetch 留窗口。 */
const RETIRE_REVOKE_DELAY_MS = 8000;

/** 读 GLB JSON chunk 的 extensionsUsed；非 GLB / 损坏数据返回空表。 */
function glbExtensions(bytes: Uint8Array): string[] {
    if (bytes.length < 20) return [];
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (view.getUint32(0, true) !== 0x46546c67) return []; // 'glTF'
    const jsonLen = view.getUint32(12, true);
    try {
        const json = JSON.parse(new TextDecoder().decode(bytes.subarray(20, 20 + jsonLen)));
        return Array.isArray(json?.extensionsUsed) ? json.extensionsUsed : [];
    } catch {
        return [];
    }
}

/** fetch 全量字节 + 停滞看门狗（每收到新字节重置计时）。失败/停滞返回
 *  null，调用方按「原 URL 直通」处理，让引擎沿自身加载路径报错。 */
async function fetchBytes(url: string, stallMs: number): Promise<Uint8Array | null> {
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | null = setTimeout(() => controller.abort(), stallMs);
    const rearm = () => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => controller.abort(), stallMs);
    };
    try {
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) return null;
        if (!res.body) return new Uint8Array(await res.arrayBuffer());
        const reader = res.body.getReader();
        const parts: Uint8Array[] = [];
        for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            if (value) { parts.push(value); rearm(); }
        }
        const out = new Uint8Array(parts.reduce((n, p) => n + p.byteLength, 0));
        let off = 0;
        for (const p of parts) { out.set(p, off); off += p.byteLength; }
        return out;
    } catch {
        return null;
    } finally {
        if (timer) clearTimeout(timer);
    }
}

/**
 * 把任意模型 URL 换成 PlayCanvas 可解析的地址：
 * 压缩模型 → 解码后的 Blob URL；普通模型 → 原 URL 原样返回。
 * fetch 失败/停滞时也原样返回，让引擎沿自身加载路径报错（不吞错）。
 */
export async function ensurePlayableGlb(url: string): Promise<string> {
    const cached = decoded.get(url);
    if (cached) return cached;
    const inflight = pending.get(url);
    if (inflight) return inflight;
    const task = transcodeIfCompressed(url);
    pending.set(url, task);
    try {
        return await task;
    } finally {
        pending.delete(url);
    }
}

async function transcodeIfCompressed(url: string): Promise<string> {
    // 优先 Worker 转码：解码/dequantize/重建的 ~160MB 瞬时峰值发生在
    // Worker 独立堆（用完即销毁、整体归还），主线程只经 transferable
    // 零拷贝收最终字节——手机端（iOS jetsam）叠加 gsplat 场景内存时的
    // 闪退正是主线程峰值所致。Worker 不可用/失败再回退主线程路径。
    const viaWorker = await transcodeInWorker(url);
    if (viaWorker !== null) return viaWorker;
    return transcodeOnMainThread(url);
}

/** 单次转码专用 Worker：完成（或失败）即 terminate，中间副本随 Worker
 *  销毁整体归还。返回 null 表示 Worker 路径不可用（创建失败/超时/报错），
 *  由调用方回退主线程转码；返回 url 表示直通（非压缩模型）。 */
function transcodeInWorker(url: string): Promise<string | null> {
    return new Promise((resolve) => {
        let worker: Worker;
        try {
            // 注意 .ts 后缀：dev 下直接命中 vite 转换的源模块（.js 会 404）；
            // build 时 vite worker 插件按此 URL 解析打包，两态一致。
            worker = new Worker(new URL('./glbMeshoptWorker.ts', import.meta.url), { type: 'module' });
        } catch {
            resolve(null);
            return;
        }
        const finish = (result: string | null) => {
            worker.terminate();
            resolve(result);
        };
        // 超时兜底：Worker 卡死（老设备 wasm 初始化异常等）按不可用回退。
        const timer = setTimeout(() => finish(null), 120000);
        worker.onmessage = (e: MessageEvent) => {
            clearTimeout(timer);
            const { ok, passthrough, bytes } = e.data as { ok: boolean; passthrough?: boolean; bytes?: Uint8Array };
            if (passthrough) { finish(url); return; }
            if (!ok || !bytes) { finish(null); return; }
            // bytes 为 transfer 而来的完整 ArrayBuffer（writeBinary 新建、无
            // 偏移），Blob 化一次后原引用交给 GC。
            finish(URL.createObjectURL(new Blob([bytes.buffer as ArrayBuffer], { type: 'model/gltf-binary' })));
        };
        worker.onerror = () => { clearTimeout(timer); finish(null); };
        worker.postMessage({ url, stallMs: FETCH_STALL_MS });
    });
}

/** 主线程转码（Worker 不可用时的回退路径，语义与旧版一致）。 */
async function transcodeOnMainThread(url: string): Promise<string> {
    const bytes = await fetchBytes(url, FETCH_STALL_MS);
    if (!bytes) return url;
    const exts = glbExtensions(bytes);
    if (!exts.includes('EXT_meshopt_compression') && !exts.includes('KHR_mesh_quantization')) return url;

    const [{ WebIO }, { EXTMeshoptCompression, KHRMeshQuantization, KHRTextureTransform }, { dequantize }, { MeshoptDecoder }] =
        await Promise.all([
            import('@gltf-transform/core'),
            import('@gltf-transform/extensions'),
            import('@gltf-transform/functions'),
            import('meshoptimizer'),
        ]);
    await MeshoptDecoder.ready;
    const io = new WebIO()
        .registerExtensions([EXTMeshoptCompression, KHRMeshQuantization, KHRTextureTransform])
        .registerDependencies({ 'meshopt.decoder': MeshoptDecoder });
    const doc = await io.readJSON(await io.binaryToJSON(bytes));
    await doc.transform(dequantize());
    // 数据已解码为标准格式：摘掉压缩扩展实例，避免写出时再走压缩链路。
    doc.createExtension(EXTMeshoptCompression).dispose();
    doc.createExtension(KHRMeshQuantization).dispose();
    const out = await io.writeBinary(doc);
    const blobUrl = URL.createObjectURL(new Blob([out], { type: 'model/gltf-binary' }));
    decoded.set(url, blobUrl);
    return blobUrl;
}

/**
 * 退役转码产物：引擎资产解析完成后调用（AvatarAssetCache）——字节已无人
 * 需要，撤销 Blob 释放内存。立即移出结果缓存（后续 ensurePlayableGlb 重新
 * 转码，HTTP 缓存保证零网络成本），撤销延迟 RETIRE_REVOKE_DELAY_MS 执行，
 * 让并发的其它消费者（预览舞台）在途 fetch 完成。
 */
export function retireDecoded(url: string): void {
    const blobUrl = decoded.get(url);
    if (!blobUrl) return;
    decoded.delete(url);
    setTimeout(() => URL.revokeObjectURL(blobUrl), RETIRE_REVOKE_DELAY_MS);
}
